import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@eo/shared';

import { ROLE_BAR_CLASS, ROLE_DOT_CLASS } from './constants';
import { formatHours } from './helpers';
import { LoadBar } from './LoadBar';
import { LoggedBreakdown } from './LoggedBreakdown';
import type { DeliveryRow, TeamDeliveryProps } from './types';

// The client's total, its fixed and variable folded under it, then the CFO and
// the Data team that make it up, the Data team's people folded under it. One
// scale throughout, so a longer bar is always more hours. Only logged hours
// fill a bar: a faded "still to log" fill ended exactly at the tick and read as
// progress.
export function TeamDelivery({ delivery, personHref, testId }: TeamDeliveryProps) {
  const [open, setOpen] = useState(false);
  const { total, buckets, owner, cfo, dataTeam, people } = delivery;
  // Variable past what is held is what gets billed, so it opens already split.
  const [totalOpen, setTotalOpen] = useState(() =>
    buckets.some((b) => b.key === 'variable' && b.logged.cfo + b.logged.dataTeam > b.held),
  );

  const fold = (isOpen: boolean, toggle: () => void, name: ReactNode, className?: string) => (
    <button
      type="button"
      aria-expanded={isOpen}
      onClick={toggle}
      className={cn(
        'flex items-center gap-2 rounded-8 text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus',
        className,
      )}
    >
      <ChevronRight
        aria-hidden
        className={cn('size-3 text-text-tertiary transition-transform', isOpen && 'rotate-90')}
      />
      {name}
    </button>
  );
  const scale = Math.max(
    0,
    ...[total, cfo, dataTeam, ...people].map((r) => Math.max(r.held, r.logged)),
  );

  const row = (
    key: string,
    label: string,
    name: ReactNode,
    held: number,
    logged: number,
    segments: { key: string; hours: number; className: string }[],
    className?: string,
    details?: ReactNode,
  ) => {
    const over = logged - held;
    return (
      <li key={key} className={cn('flex flex-col gap-2', className)}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
          {name}
          <span className="tabular-nums text-text-secondary">
            {formatHours(logged)} of {formatHours(held)}
            {over > 0 && <span className="ml-2 text-destructive">{formatHours(over)} over</span>}
          </span>
        </div>
        <LoadBar
          label={`${label}: ${formatHours(logged)} logged of ${formatHours(held)} held`}
          scale={scale}
          target={held}
          targetHint={`Held ${formatHours(held)}: the hours ${label} holds this month. Hours logged past this line are over.`}
          segments={segments}
          details={details}
        />
      </li>
    );
  };
  const side = (r: DeliveryRow, name: ReactNode, className?: string) =>
    row(
      r.key,
      r.label,
      name,
      r.held,
      r.logged,
      [{ key: 'logged', hours: r.logged, className: ROLE_BAR_CLASS[r.role].logged }],
      className,
    );
  const personLink =
    'flex items-center gap-2 rounded-8 text-text-primary underline-offset-2 hover:text-text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus';
  const dot = (r: DeliveryRow) => (
    <span className={cn('size-2.5 shrink-0 rounded-[2px]', ROLE_DOT_CLASS[r.role])} />
  );

  return (
    <ul data-testid={testId} className="flex flex-col gap-4">
      {row(
        'total',
        'Total',
        fold(totalOpen, () => setTotalOpen((o) => !o), <span className="font-medium">Total</span>),
        total.held,
        total.logged,
        [cfo, dataTeam].map((r) => ({
          key: r.key,
          hours: r.logged,
          className: ROLE_BAR_CLASS[r.role].logged,
        })),
      )}
      {totalOpen &&
        buckets.map((b) =>
          row(
            b.key,
            b.label,
            <span className="text-text-secondary">{b.label}</span>,
            b.held,
            b.logged.cfo + b.logged.dataTeam,
            (['cfo', 'dataTeam'] as const).map((role) => ({
              key: role,
              hours: b.logged[role],
              className: ROLE_BAR_CLASS[role].logged,
            })),
            'pl-5',
            <LoggedBreakdown
              byCfo={b.logged.cfo}
              byData={b.logged.dataTeam}
              logged={b.logged.cfo + b.logged.dataTeam}
              budget={b.held}
            />,
          ),
        )}
      {side(
        cfo,
        // The owner holds the CFO side, so it opens the owner's log.
        owner ? (
          <Link
            to={{ search: personHref(owner.userId) }}
            aria-label={`${owner.name}’s time log`}
            className={personLink}
          >
            {dot(cfo)}
            {cfo.label}
            <ChevronRight aria-hidden className="size-3 text-text-tertiary" />
          </Link>
        ) : (
          <span className="flex items-center gap-2 text-text-primary">
            {dot(cfo)}
            {cfo.label}
          </span>
        ),
        'pl-5',
      )}
      {side(
        dataTeam,
        people.length > 0 ? (
          fold(
            open,
            () => setOpen((o) => !o),
            <>
              {dot(dataTeam)}
              {dataTeam.label}
            </>,
            '-ml-5',
          )
        ) : (
          <span className="flex items-center gap-2 text-text-primary">
            {dot(dataTeam)}
            {dataTeam.label}
          </span>
        ),
        'pl-5',
      )}
      {open &&
        people.map((p) =>
          side(
            p,
            <Link
              to={{ search: personHref(p.key) }}
              aria-label={`${p.label}’s time log`}
              className={cn(personLink, 'gap-1 text-text-secondary')}
            >
              {p.label}
              <ChevronRight aria-hidden className="size-3 text-text-tertiary" />
            </Link>,
            'pl-10',
          ),
        )}
    </ul>
  );
}
